import { Component } from '@theme/component';

/**
 * Oficina do Iô: slider automático da faixa de confiança no celular (sections/oi-guarantees.liquid).
 *
 * A lista é uma rolagem lateral com encaixe, um item por vez; sem JavaScript ela continua deslizando
 * com o dedo. Aqui a lista avança sozinha a cada `autoplay` segundos e os pontos mostram e escolhem o
 * item. No desktop (a partir de 750 px) a faixa é uma grade e o componente fica parado.
 *
 * O avanço pausa com o ponteiro em cima, o foco dentro, o dedo na lista, a aba escondida ou a faixa
 * fora da tela, e para de vez pelo botão de pausar (atributo `paused`) até ser retomado por ele; com
 * `prefers-reduced-motion`, não há avanço automático.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - A lista que rola.
 * @property {HTMLElement[]} slides - Os itens.
 * @property {HTMLButtonElement[]} [dots] - Um ponto por item.
 * @property {HTMLButtonElement} [toggle] - Pausar/retomar o avanço automático.
 *
 * @extends {Component<Refs>}
 */
class OiGuaranteesSliderComponent extends Component {
  requiredRefs = ['track', 'slides'];

  #current = 0;

  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {AbortController|undefined} */
  #abortController = undefined;

  /** @type {IntersectionObserver|undefined} */
  #observer = undefined;

  #visible = false;

  #touching = false;

  /** Pausado pelo botão: só o próprio botão retoma. */
  #stopped = false;

  #mobile = window.matchMedia('(max-width: 749px)');

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { track } = this.refs;

    track.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    track.addEventListener('pointerdown', () => (this.#touching = true), { signal });
    for (const type of ['pointerup', 'pointercancel']) {
      track.addEventListener(type, this.#handleRelease, { signal });
    }
    this.#mobile.addEventListener('change', this.#restart, { signal });
    this.#reducedMotion.addEventListener('change', this.#restart, { signal });

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
    });
    this.#observer.observe(this);

    this.#current = this.#indexFromScroll();
    this.#updateDots();
    this.#restart();
  }

  updatedCallback() {
    super.updatedCallback();

    this.#current = this.#indexFromScroll();
    this.#updateDots();
    this.#syncToggle();
    this.#restart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#scrollTimeout);
    this.#stop();
  }

  /**
   * Mostra o item escolhido pelo ponto e recomeça a contagem.
   *
   * @param {number} index - Posição do item, a partir de 0.
   */
  goTo(index) {
    this.#scrollTo(index);
    this.#restart();
  }

  /** Pausa ou retoma o avanço automático (botão ao lado dos pontos). */
  togglePlayback() {
    this.#stopped = !this.#stopped;
    this.#syncToggle();
    this.#restart();
  }

  /** Atributo `paused` (troca o ícone) e nome do botão de acordo com o estado. */
  #syncToggle() {
    this.toggleAttribute('paused', this.#stopped);

    const { toggle } = this.refs;
    const label = this.#stopped ? toggle?.dataset.labelPlay : toggle?.dataset.labelPause;
    if (toggle && label) toggle.setAttribute('aria-label', label);
  }

  get #autoplayInterval() {
    const value = parseInt(`${this.getAttribute('autoplay')}`, 10);

    return Number.isNaN(value) || value <= 0 ? undefined : value * 1000;
  }

  get #paused() {
    return document.hidden || !this.#visible || this.#touching || this.matches(':hover, :focus-within');
  }

  #restart = () => {
    this.#stop();

    const interval = this.#autoplayInterval;
    if (!interval || this.#stopped || !this.#mobile.matches || this.#reducedMotion.matches) return;

    this.#interval = setInterval(() => {
      if (this.#paused) return;

      this.#scrollTo(this.#current + 1);
    }, interval);
  };

  #stop() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * @param {number} index - Posição do item; volta ao primeiro depois do último.
   */
  #scrollTo(index) {
    const { track, slides } = this.refs;
    const count = slides.length;
    if (!count) return;

    this.#current = ((index % count) + count) % count;
    this.#updateDots();

    // Em RTL, `scrollLeft` cresce para a esquerda (valores negativos).
    const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollTo({
      left: direction * this.#current * track.clientWidth,
      behavior: this.#reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  #indexFromScroll() {
    const { track, slides } = this.refs;
    if (!track.clientWidth) return 0;

    return Math.min(Math.round(Math.abs(track.scrollLeft) / track.clientWidth), slides.length - 1);
  }

  #updateDots() {
    this.refs.dots?.forEach((dot, index) => {
      if (index === this.#current) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /** @type {number|undefined} */
  #scrollTimeout = undefined;

  /** Quando a rolagem para (arraste ou avanço), o ponto passa para o item que ficou na tela. */
  #handleScroll = () => {
    clearTimeout(this.#scrollTimeout);
    this.#scrollTimeout = setTimeout(() => {
      this.#current = this.#indexFromScroll();
      this.#updateDots();
    }, 100);
  };

  /** Ao soltar o dedo, recomeça a contagem. */
  #handleRelease = () => {
    this.#touching = false;
    this.#restart();
  };
}

if (!customElements.get('oi-guarantees-slider-component')) {
  customElements.define('oi-guarantees-slider-component', OiGuaranteesSliderComponent);
}
